import { useMemo } from 'react';
import { useStore } from '../store/useStore';
import { SubscriptionPlan } from '../types';

export function useEntitlements() {
    const license = useStore((state) => state.license);
    const subscriptionPlans = useStore((state) => state.subscriptionPlans);
    const users = useStore((state) => state.users);
    const user = useStore((state) => state.user);

    const isTrial = license?.status === 'trialing';

    const currentPlan = useMemo(() => {
        if (!license?.planId) return null;
        return subscriptionPlans.find((p: SubscriptionPlan) => p.id === license.planId) as SubscriptionPlan | null;
    }, [license?.planId, subscriptionPlans]);

    // Derived Limits
    const maxUsers = currentPlan?.maxUsers || 1;
    const currentUsersCount = users.filter(u => u.tenantId === license?.tenantId).length;

    // Check if expired
    const isExpired = useMemo(() => {
        if (user?.role === 'superadmin') return false; // Superadmins never expire
        if (!license) return false;
        if (license.status === 'past_due') return true;
        if (license.expirationDate) {
            return new Date(license.expirationDate).getTime() < new Date().getTime();
        }
        return false;
    }, [license, user?.role]);

    const isReadOnly = isExpired;
    const canAddUser = (isTrial ? true : currentUsersCount < maxUsers) && !isReadOnly;

    const getModulesArray = () => {
        const modules: any = currentPlan?.modules;
        if (!modules) return [];
        if (Array.isArray(modules)) return modules;
        if (typeof modules === 'string') {
            try {
                const parsed = JSON.parse(modules);
                if (Array.isArray(parsed)) return parsed;
                if (parsed && typeof parsed === 'object') return Object.values(parsed);
            } catch (e) {
                // Ignore parse errors, proceed to string fallback
            }

            // Fallback: bracket-wrapped or comma-separated list
            let clean = modules.trim();
            if (clean.startsWith('[') && clean.endsWith(']')) {
                clean = clean.substring(1, clean.length - 1);
            }
            if (clean.includes(',')) {
                return clean.split(',').map(m => m.replace(/['"]+/g, '').trim()).filter(Boolean);
            }
            return [clean.replace(/['"]+/g, '').trim()].filter(Boolean);
        }
        if (modules && typeof modules === 'object') {
            const keys = Object.keys(modules);
            if (keys.length > 0 && typeof (modules as any)[keys[0]] === 'boolean') {
                return keys.filter(key => (modules as any)[key] === true);
            }
            return Object.values(modules);
        }
        return [];
    };

    // Evaluate if a specific module is accessible
    const hasModuleAccess = (moduleName: string) => {
        if (user?.role === 'superadmin') return true;
        if (isTrial) return true; // Full access during trial
        return getModulesArray().includes(moduleName);
    };

    // Helper to determine if an icon/crown should be shown
    const isPremiumModuleWarning = (moduleName: string) => {
        if (!isTrial) return false;
        // If they are in trial, we show the crown IF the module is NOT part of their currently selected plan.
        return !getModulesArray().includes(moduleName);
    };

    return {
        isTrial,
        currentPlan,
        maxUsers,
        currentUsersCount,
        canAddUser,
        hasModuleAccess,
        isPremiumModuleWarning,
        isExpired,
        isReadOnly
    };
}
